import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { Heart, Calendar, Send, FileText, Download, Star, Ticket, ArrowUpRight, Sparkles, Pause, Play, X, RefreshCw, Crown, Award, Phone, Check, Lock } from "lucide-react";
import { LevelBadge, TIER_PERKS, MilestoneBadge } from "@/components/LevelBadge";
import { LevelUpModal } from "@/components/LevelUpModal";
import { MilestoneModal } from "@/components/MilestoneModal";
import { toast } from "sonner";

const TIERS = [
  { key: "give_25", amount: 25 }, { key: "give_50", amount: 50 },
  { key: "give_100", amount: 100 }, { key: "give_250", amount: 250 }, { key: "give_500", amount: 500 },
];

export default function SupporterDashboard() {
  const { user } = useAuth();
  const [selected, setSelected] = useState("give_100");
  const [customAmount, setCustomAmount] = useState("");
  const [monthly, setMonthly] = useState(false);
  const [giving, setGiving] = useState(false);
  const [donations, setDonations] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [impact, setImpact] = useState(null);
  const [subs, setSubs] = useState([]);
  const [subBusy, setSubBusy] = useState("");
  const [status, setStatus] = useState(null);
  const [leveledUp, setLeveledUp] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [pendingMilestone, setPendingMilestone] = useState(null);
  const [milestoneShare, setMilestoneShare] = useState(false);
  const [wallOptin, setWallOptin] = useState(false);
  const [callForm, setCallForm] = useState({ preferred_time: "", phone: "", note: "" });
  const [callReq, setCallReq] = useState(null);
  const [callBusy, setCallBusy] = useState(false);

  const load = () => {
    api.get("/donations/mine").then((r) => setDonations(r.data)).catch(() => {});
    api.get("/tickets/mine").then((r) => setTickets(r.data)).catch(() => {});
    api.get("/impact/story").then((r) => setImpact(r.data)).catch(() => {});
    api.get("/donations/subscriptions").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/supporter/status").then((r) => {
      setStatus(r.data);
      setWallOptin(!!r.data.wall_optin);
      if (r.data.leveled_up) setLeveledUp(r.data.leveled_up);
      setMilestones(r.data.milestones || []);
      if (r.data.pending_milestone) setPendingMilestone(r.data.pending_milestone);
      setMilestoneShare(!!r.data.milestone_share);
      if ((r.data.level_rank ?? 0) >= 3) api.get("/supporter/thankyou-call").then((c) => setCallReq(c.data?.id ? c.data : null)).catch(() => {});
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const rank = status?.level_rank ?? 0;

  const toggleWall = async () => {
    try {
      const { data } = await api.put("/profile", { wall_optin: !wallOptin });
      setWallOptin(!!data.wall_optin);
      toast.success(data.wall_optin ? "You'll be honored on the Supporter Wall." : "Removed from the Supporter Wall.");
    } catch { toast.error("Could not update"); }
  };

  const toggleMilestoneShare = async () => {
    try {
      const { data } = await api.put("/profile", { milestone_share: !milestoneShare });
      setMilestoneShare(!!data.milestone_share);
      toast.success(data.milestone_share ? "Milestones will be shared to the community feed." : "Milestone sharing turned off.");
    } catch { toast.error("Could not update"); }
  };

  const requestCall = async () => {
    if (!callForm.preferred_time.trim()) { toast.error("Add a preferred time"); return; }
    setCallBusy(true);
    try {
      const { data } = await api.post("/supporter/thankyou-call", {
        name: user.name, email: user.email, subject: callForm.preferred_time,
        message: `${callForm.phone} | ${callForm.note}`,
      });
      setCallReq(data.request);
      toast.success("Your thank-you call is requested. We'll be in touch!");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCallBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const manageSub = async (id, action) => {
    setSubBusy(id + action);
    try {
      await api.post(`/donations/subscriptions/${id}/${action}`);
      const labels = { pause: "Monthly gift paused", resume: "Monthly gift resumed", cancel: "Monthly gift canceled" };
      toast.success(labels[action]);
      const { data } = await api.get("/donations/subscriptions");
      setSubs(data);
    } catch { toast.error("Could not update your monthly gift"); }
    finally { setSubBusy(""); }
  };

  const give = async () => {
    setGiving(true);
    const amt = customAmount ? Number(customAmount) : null;
    const body = { origin_url: window.location.origin, recurring: monthly };
    if (monthly) body.amount = amt || TIERS.find((t) => t.key === selected).amount;
    else if (amt) body.amount = amt;
    else body.lookup_key = selected;
    try {
      const { data } = await api.post("/donations/checkout", body);
      window.location.href = data.checkout_url;
    } catch { toast.error("Could not start checkout"); setGiving(false); }
  };

  const downloadStatement = (fmt) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/donations/statement.${fmt}`, "_blank");
  };

  const submitRating = async () => {
    if (!stars) { toast.error("Select a star rating"); return; }
    try { await api.post("/ratings", { stars, text: reviewText }); toast.success("Thank you for your rating!"); }
    catch { toast.error("Could not submit"); }
  };

  const totalGiven = donations.reduce((s, d) => s + (d.amount || 0), 0);

  return (
    <div className="min-h-screen bg-bone pt-32 pb-24">
      <LevelUpModal levelKey={leveledUp} onClose={() => setLeveledUp(null)} />
      <MilestoneModal mkey={pendingMilestone} onClose={() => setPendingMilestone(null)} />
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-black">Supporter Portal</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">
            Thank you, {user?.name?.split(" ")[0]}.
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-5">
            {status && <LevelBadge level={status.level_key} size="lg" data-testid="supporter-level-badge" />}
            {status?.next_level && (
              <span className="font-sans text-sm text-charcoal/55" data-testid="level-progress">
                ${Number(status.to_next).toLocaleString()} more to reach <span className="text-navy font-medium">{status.next_level}</span>
              </span>
            )}
            {status && !status.next_level && (
              <span className="font-sans text-sm text-charcoal/55">Our highest tier — thank you for your faithfulness.</span>
            )}
          </div>
          <p className="font-sans text-charcoal/60 mt-4 max-w-xl">Your partnership fuels this ministry. Give, join events, and share your voice.</p>
        </Reveal>

        {/* Give */}
        <Reveal delay={0.05}>
          <section className="mt-14 border border-charcoal/10" data-testid="give-section">
            <div className="grid grid-cols-1 md:grid-cols-12">
              <div className="md:col-span-7 p-8 md:p-12">
                <div className="flex items-center gap-3"><Heart size={22} className="text-rust" strokeWidth={1.5} /><h2 className="font-serif text-3xl tracking-tight">Support the Ministry</h2></div>
                <p className="font-sans text-charcoal/60 mt-3">Choose a one-time gift. Secure checkout by Stripe.</p>
                <div className="flex flex-wrap gap-3 mt-8">
                  {TIERS.map((t) => (
                    <button key={t.key} onClick={() => { setSelected(t.key); setCustomAmount(""); }} data-testid={`tier-${t.key}`}
                      className={`px-6 py-4 font-serif text-2xl border transition-colors ${selected === t.key && !customAmount ? "bg-navy text-bone border-navy" : "border-charcoal/20 hover:border-charcoal"}`}>
                      ${t.amount}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-6 mt-6">
                  <div className="flex items-center border-b border-charcoal/30 focus-within:border-navy transition-colors">
                    <span className="font-serif text-2xl text-charcoal/50 pr-1">$</span>
                    <input type="number" min="1" step="1" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} placeholder="Other amount" data-testid="custom-amount"
                      className="w-40 bg-transparent py-2 font-serif text-2xl focus:outline-none" />
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="monthly-toggle">
                    <input type="checkbox" checked={monthly} onChange={(e) => setMonthly(e.target.checked)} className="accent-navy w-4 h-4" />
                    <span className="font-mono text-xs uppercase tracking-widest text-charcoal/70">Give monthly (partnership)</span>
                  </label>
                </div>
                <button onClick={give} disabled={giving} data-testid="give-btn"
                  className="group inline-flex items-center gap-3 mt-8 bg-charcoal text-bone px-10 py-4 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">
                  {giving ? "Redirecting..." : monthly ? "Start Monthly Gift" : "Give Now"} <ArrowUpRight size={16} className="hover-arrow" />
                </button>
              </div>
              <div className="md:col-span-5 bg-navy text-bone p-8 md:p-12 grain relative flex flex-col justify-center">
                <span className="font-mono text-xs uppercase tracking-widest text-gold">Your Giving</span>
                <span className="font-serif text-6xl mt-3">${totalGiven.toFixed(0)}</span>
                <span className="font-sans text-bone/60 mt-1">across {donations.length} gift{donations.length === 1 ? "" : "s"}</span>
              </div>
            </div>
          </section>
        </Reveal>

        {/* Impact Story */}
        <Reveal delay={0.08}>
          <section className="mt-8 bg-navy text-bone grain relative overflow-hidden" data-testid="impact-section">
            <div className="p-8 md:p-12">
              <div className="flex items-center gap-3">
                <Sparkles size={20} className="text-gold" strokeWidth={1.5} />
                <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">What Your Giving Made Possible</span>
              </div>
              <p className="font-serif text-2xl md:text-3xl leading-snug tracking-tight mt-6 max-w-3xl text-bone" data-testid="impact-story">
                {impact ? impact.story : "Gathering your impact story…"}
              </p>
              {impact && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-bone/10 mt-10 border border-bone/10">
                  {impact.tiles.map((t, i) => (
                    <div key={i} className="bg-navy p-6" data-testid={`impact-tile-${i}`}>
                      <div className="font-serif text-4xl text-gold">{t.value}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-bone/60 mt-2">{t.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </Reveal>

        {/* Recurring Manager */}
        {subs.length > 0 && (
          <Reveal delay={0.1}>
            <section className="mt-8 border border-charcoal/10 p-8" data-testid="recurring-section">
              <div className="flex items-center gap-3">
                <RefreshCw size={20} className="text-navy" strokeWidth={1.5} />
                <h2 className="font-serif text-2xl tracking-tight">Monthly Partnership</h2>
              </div>
              <p className="font-sans text-sm text-charcoal/60 mt-2">Manage your recurring gifts anytime — pause, resume, or cancel.</p>
              <div className="mt-6 space-y-px bg-charcoal/10 border border-charcoal/10">
                {subs.map((s) => {
                  const canceled = s.status === "canceled";
                  const paused = s.status === "paused";
                  return (
                    <div key={s.subscription_id} className="bg-bone p-5 flex items-center justify-between flex-wrap gap-4" data-testid={`sub-${s.subscription_id}`}>
                      <div>
                        <span className="font-serif text-2xl">${Number(s.amount).toFixed(0)}</span>
                        <span className="font-mono text-xs text-charcoal/50 ml-2">/ month</span>
                        <span className={`font-mono text-[10px] uppercase tracking-widest ml-4 ${canceled ? "text-rust" : paused ? "text-gold" : "text-olive"}`}>
                          {canceled ? "Canceled" : paused ? "Paused" : "Active"}
                        </span>
                      </div>
                      {!canceled && (
                        <div className="flex items-center gap-2">
                          {paused ? (
                            <button onClick={() => manageSub(s.subscription_id, "resume")} disabled={subBusy} data-testid={`sub-resume-${s.subscription_id}`}
                              className="inline-flex items-center gap-2 border border-charcoal/30 px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-charcoal hover:text-bone transition-colors disabled:opacity-40"><Play size={12} /> Resume</button>
                          ) : (
                            <button onClick={() => manageSub(s.subscription_id, "pause")} disabled={subBusy} data-testid={`sub-pause-${s.subscription_id}`}
                              className="inline-flex items-center gap-2 border border-charcoal/30 px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-charcoal hover:text-bone transition-colors disabled:opacity-40"><Pause size={12} /> Pause</button>
                          )}
                          <button onClick={() => { if (window.confirm("Cancel this monthly gift? This cannot be undone.")) manageSub(s.subscription_id, "cancel"); }} disabled={subBusy} data-testid={`sub-cancel-${s.subscription_id}`}
                            className="inline-flex items-center gap-2 border border-rust/40 text-rust px-4 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-rust hover:text-bone transition-colors disabled:opacity-40"><X size={12} /> Cancel</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </Reveal>
        )}

        {/* Partner Rewards */}
        <Reveal delay={0.1}>
          <section className="mt-8 border border-charcoal/10 p-8" data-testid="rewards-section">
            <div className="flex items-center gap-3">
              <Award size={20} className="text-gold" strokeWidth={1.5} />
              <h2 className="font-serif text-2xl tracking-tight">Your Rewards</h2>
            </div>
            <p className="font-sans text-sm text-charcoal/60 mt-2">
              {status?.next_level
                ? `You're a ${status?.level} partner. Reach ${status?.next_level} to unlock more.`
                : `You've unlocked every reward. Thank you.`}
            </p>

            {/* Milestones */}
            <div className="mt-6 border border-gold/25 bg-gold/[0.04] p-5" data-testid="milestones-strip">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-charcoal/50">Giving Milestones</span>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                {["gift_250", "gift_1000", "year_1"].map((k) => {
                  const earned = milestones.includes(k);
                  return (
                    <div key={k} className={earned ? "" : "opacity-35 grayscale"} data-testid={`milestone-${k}-${earned ? "earned" : "locked"}`}>
                      <MilestoneBadge mkey={k} size="md" />
                    </div>
                  );
                })}
                {milestones.length === 0 && <span className="font-sans text-xs text-charcoal/45">Give to start earning milestone badges.</span>}
              </div>
              <label className="mt-4 pt-4 border-t border-charcoal/10 flex items-center gap-3 cursor-pointer" data-testid="milestone-share-toggle">
                <input type="checkbox" checked={milestoneShare} onChange={toggleMilestoneShare} className="accent-navy w-4 h-4" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/70">Celebrate my milestones on the community feed</span>
              </label>
            </div>

            {/* Perk ladder */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-charcoal/10 border border-charcoal/10 mt-6">
              {["basic", "silver", "gold", "platinum"].map((k) => {
                const idx = { basic: 0, silver: 1, gold: 2, platinum: 3 }[k];
                const unlocked = rank >= idx;
                return (
                  <div key={k} className={`p-5 ${unlocked ? "bg-bone" : "bg-sand/40"}`} data-testid={`perk-col-${k}`}>
                    <div className="flex items-center gap-2">
                      <LevelBadge level={k} size="sm" />
                      {!unlocked && <Lock size={12} className="text-charcoal/40" />}
                    </div>
                    <ul className="mt-4 space-y-2">
                      {TIER_PERKS[k].map((p) => (
                        <li key={p} className={`flex items-start gap-2 font-sans text-xs ${unlocked ? "text-charcoal/70" : "text-charcoal/40"}`}>
                          <Check size={13} className={`mt-0.5 shrink-0 ${unlocked ? "text-olive" : "text-charcoal/30"}`} /> {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            {/* Reward actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {/* Prayer Circle */}
              <div className={`border p-6 flex flex-col ${rank >= 2 ? "border-gold/40 bg-gold/5" : "border-charcoal/10"}`} data-testid="reward-circle">
                <Crown size={22} className={rank >= 2 ? "text-gold" : "text-charcoal/30"} strokeWidth={1.5} />
                <h3 className="font-serif text-xl tracking-tight mt-4">Gold Prayer Circle</h3>
                <p className="font-sans text-sm text-charcoal/55 mt-1 flex-1">A private space for Gold & Platinum partners to pray together.</p>
                {rank >= 2 ? (
                  <Link to="/circle" data-testid="enter-circle" className="group inline-flex items-center gap-2 mt-4 font-mono text-xs uppercase tracking-widest text-navy hover:gap-3 transition-all">
                    Enter the Circle <ArrowUpRight size={14} className="hover-arrow" />
                  </Link>
                ) : (
                  <span className="mt-4 font-mono text-[10px] uppercase tracking-widest text-charcoal/40 flex items-center gap-2"><Lock size={12} /> Unlocks at Gold</span>
                )}
              </div>

              {/* Supporter Wall opt-in */}
              <div className={`border p-6 flex flex-col ${rank >= 2 ? "border-charcoal/10" : "border-charcoal/10"}`} data-testid="reward-wall">
                <Award size={22} className={rank >= 2 ? "text-gold" : "text-charcoal/30"} strokeWidth={1.5} />
                <h3 className="font-serif text-xl tracking-tight mt-4">Supporter Wall</h3>
                <p className="font-sans text-sm text-charcoal/55 mt-1 flex-1">Be honored publicly on our honor roll of faithful partners.</p>
                {rank >= 2 ? (
                  <label className="mt-4 flex items-center gap-3 cursor-pointer" data-testid="wall-optin-toggle">
                    <input type="checkbox" checked={wallOptin} onChange={toggleWall} className="accent-navy w-4 h-4" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/70">{wallOptin ? "You're on the wall" : "Show me on the wall"}</span>
                  </label>
                ) : (
                  <span className="mt-4 font-mono text-[10px] uppercase tracking-widest text-charcoal/40 flex items-center gap-2"><Lock size={12} /> Unlocks at Gold</span>
                )}
              </div>

              {/* Platinum thank-you call */}
              <div className={`border p-6 flex flex-col ${rank >= 3 ? "border-navy/30 bg-navy/5" : "border-charcoal/10"}`} data-testid="reward-call">
                <Phone size={22} className={rank >= 3 ? "text-navy" : "text-charcoal/30"} strokeWidth={1.5} />
                <h3 className="font-serif text-xl tracking-tight mt-4">Thank-You Call</h3>
                <p className="font-sans text-sm text-charcoal/55 mt-1 flex-1">A personal call from our ministry team to thank you.</p>
                {rank < 3 ? (
                  <span className="mt-4 font-mono text-[10px] uppercase tracking-widest text-charcoal/40 flex items-center gap-2"><Lock size={12} /> Unlocks at Platinum</span>
                ) : callReq ? (
                  <span className="mt-4 font-mono text-[10px] uppercase tracking-widest text-olive flex items-center gap-2" data-testid="call-requested"><Check size={13} /> Requested — we'll be in touch</span>
                ) : (
                  <div className="mt-4 space-y-2">
                    <input value={callForm.preferred_time} onChange={(e) => setCallForm({ ...callForm, preferred_time: e.target.value })} placeholder="Best time to call" data-testid="call-time"
                      className="w-full border-b border-charcoal/30 bg-transparent py-1.5 font-sans text-sm focus:outline-none focus:border-navy" />
                    <input value={callForm.phone} onChange={(e) => setCallForm({ ...callForm, phone: e.target.value })} placeholder="Phone number" data-testid="call-phone"
                      className="w-full border-b border-charcoal/30 bg-transparent py-1.5 font-sans text-sm focus:outline-none focus:border-navy" />
                    <button onClick={requestCall} disabled={callBusy} data-testid="request-call-btn"
                      className="mt-2 w-full bg-navy text-bone py-2.5 font-mono text-[10px] uppercase tracking-widest hover:bg-charcoal transition-colors disabled:opacity-50">
                      {callBusy ? "Requesting..." : "Request my call"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
          <Reveal delay={0.1}>
            <section className="border border-charcoal/10 p-8 h-full" data-testid="statements-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3"><FileText size={20} className="text-navy" strokeWidth={1.5} /><h2 className="font-serif text-2xl tracking-tight">Giving Statements</h2></div>
                <div className="flex gap-2">
                  <button onClick={() => downloadStatement("pdf")} data-testid="download-statement-pdf" className="inline-flex items-center gap-2 bg-navy text-bone px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-charcoal transition-colors"><Download size={13} /> PDF</button>
                  <button onClick={() => downloadStatement("csv")} data-testid="download-statement" className="inline-flex items-center gap-2 border border-charcoal/30 px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-charcoal hover:text-bone transition-colors"><Download size={13} /> CSV</button>
                </div>
              </div>
              <div className="mt-6 space-y-px bg-charcoal/10 border border-charcoal/10">
                {donations.length === 0 && <div className="bg-bone p-5 font-sans text-sm text-charcoal/50">No gifts yet. Your statements will appear here.</div>}
                {donations.map((d) => (
                  <div key={d.session_id} className="bg-bone p-5 flex items-center justify-between" data-testid={`donation-${d.session_id}`}>
                    <div><span className="font-serif text-xl">${Number(d.amount).toFixed(0)}</span><span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40 ml-3">{d.created_at?.slice(0, 10)}</span></div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-gold">Receipted</span>
                  </div>
                ))}
              </div>
            </section>
          </Reveal>

          {/* Tickets + Events */}
          <Reveal delay={0.15}>
            <section className="border border-charcoal/10 p-8 h-full" data-testid="tickets-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3"><Ticket size={20} className="text-navy" strokeWidth={1.5} /><h2 className="font-serif text-2xl tracking-tight">My Tickets</h2></div>
                <Link to="/events" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-navy hover:underline"><Calendar size={13} /> Browse Events</Link>
              </div>
              <div className="mt-6 space-y-px bg-charcoal/10 border border-charcoal/10">
                {tickets.length === 0 && <div className="bg-bone p-5 font-sans text-sm text-charcoal/50">No tickets yet. Reserve your place at an upcoming event.</div>}
                {tickets.map((t) => (
                  <div key={t.id} className="bg-bone p-5 flex items-center gap-4" data-testid={`ticket-${t.id}`}>
                    <Link to={`/ticket/${t.id}`}>
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${t.id}/qr.png`} alt="Ticket QR" className="w-16 h-16 shrink-0 border border-charcoal/10" data-testid={`ticket-qr-${t.id}`} />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-lg tracking-tight">{t.event_title}</h4>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40">{t.quantity} ticket{t.quantity === 1 ? "" : "s"} · {new Date(t.event_date).toLocaleDateString()}</span>
                      <div className="flex items-center gap-3 mt-2">
                        <Link to={`/ticket/${t.id}`} data-testid={`save-ticket-${t.id}`} className="font-mono text-[10px] uppercase tracking-widest text-navy hover:underline flex items-center gap-1"><Ticket size={12} /> Save Ticket</Link>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${t.id}/pass.pdf`} target="_blank" rel="noreferrer" data-testid={`ticket-pdf-${t.id}`} className="font-mono text-[10px] uppercase tracking-widest text-charcoal/50 hover:text-navy flex items-center gap-1"><Download size={12} /> PDF</a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
          {/* Rating */}
          <Reveal delay={0.1}>
            <section className="border border-charcoal/10 p-8" data-testid="rating-section">
              <div className="flex items-center gap-3"><Star size={20} className="text-gold" strokeWidth={1.5} /><h2 className="font-serif text-2xl tracking-tight">Leave a Rating</h2></div>
              <p className="font-sans text-sm text-charcoal/60 mt-2">Your review appears on our homepage.</p>
              <div className="flex gap-2 mt-6">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setStars(n)} data-testid={`star-${n}`}>
                    <Star size={32} strokeWidth={1.3} className={(hover || stars) >= n ? "fill-gold text-gold" : "text-charcoal/30"} />
                  </button>
                ))}
              </div>
              <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} rows={3} placeholder="Share your experience (optional)" data-testid="rating-text"
                className="w-full mt-5 border-b border-charcoal/30 bg-transparent py-2 font-sans focus:outline-none focus:border-navy transition-colors resize-none" />
              <button onClick={submitRating} data-testid="submit-rating" className="mt-5 bg-charcoal text-bone px-8 py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors">Submit Rating</button>
            </section>
          </Reveal>

          {/* Messaging shortcut */}
          <Reveal delay={0.15}>
            <Link to="/messages" data-testid="supporter-messages" className="group border border-charcoal/10 p-8 flex flex-col justify-between h-full hover:bg-sand transition-colors duration-400">
              <Send size={22} className="text-navy" strokeWidth={1.5} />
              <div className="mt-16">
                <div className="flex items-center justify-between"><h2 className="font-serif text-2xl tracking-tight">Messages</h2><ArrowUpRight size={18} className="hover-arrow" /></div>
                <p className="font-sans text-sm text-charcoal/60 mt-1">Connect with the community and the ministry team.</p>
              </div>
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
